// MCHD Room Scheduler - User Avatar Dropdown Component
// Created: 07-10-2025 14:01:32
// Purpose: Top bar user menu with Profile and Logout options

import React from 'react';
import { Menu, Group, Text, Divider, Box } from '@mantine/core';
import { IconUser, IconLogout, IconChevronDown } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from './UserAvatar';

/**
 * User data interface
 */
export interface UserData {
  /** User ID */
  id: string;
  /** Full name */
  name: string;
  /** Email address */
  email: string;
  /** User role */
  role: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
}

/**
 * Props for UserAvatarDropdown component
 */
export interface UserAvatarDropdownProps {
  /** Current user data */
  user: UserData;
  /** Logout handler function */
  onLogout: () => void;
}

/**
 * UserAvatarDropdown Component
 * 
 * Top bar dropdown menu with user avatar, showing:
 * - User name and role
 * - Profile option (navigates to profile page)
 * - Logout option (triggers logout)
 * 
 * UX Touchpoint Spec Requirements:
 * - User avatar with initials (first + last initial, or "SA" for System Administrator)
 * - Dropdown menu with Profile and Logout options
 * - Accessible via keyboard (Tab, Enter, Arrow keys, Escape)
 * 
 * Features:
 * - Avatar displays user initials automatically
 * - Role badge below name
 * - Email address display
 * - Profile navigation
 * - Logout action
 * - Keyboard accessible
 * - ARIA labels for screen readers
 * 
 * @component
 * @example
 * ```tsx
 * <UserAvatarDropdown 
 *   user={{ id: '123', name: 'John Doe', email: 'john@moh.gov.sa', role: 'DOCTOR' }}
 *   onLogout={() => console.log('Logging out...')} 
 * />
 * ```
 * 
 * Created: 07-10-2025 14:01:32
 * Complies: @core-development-philosophy.mdc, @ui-ux-excellence.mdc, @typescript-quality.mdc
 */
export const UserAvatarDropdown: React.FC<UserAvatarDropdownProps> = ({
  user,
  onLogout,
}) => {
  const navigate = useNavigate();

  /**
   * Handle profile navigation
   * Routes to appropriate profile page based on user role
   * 
   * Created: 07-10-2025 14:01:32
   */
  const handleProfileClick = () => {
    const profileRoutes = {
      ADMIN: '/admin/profile',
      COORDINATOR: '/coordinator/profile',
      DOCTOR: '/doctor/profile',
    };
    
    navigate(profileRoutes[user.role]);
  };

  /**
   * Get role display label
   * Formats role enum for user-friendly display
   * 
   * @param role - User role enum
   * @returns Formatted role label
   * 
   * Created: 07-10-2025 14:01:32
   */
  const getRoleLabel = (role: string): string => {
    return role.charAt(0) + role.slice(1).toLowerCase();
  };

  return (
    <Menu
      shadow="md"
      width={280}
      position="bottom-end"
      withArrow
      arrowPosition="center"
      transitionProps={{ transition: 'pop-top-right' }}
    >
      <Menu.Target>
        <Box
          style={{
            cursor: 'pointer',
            borderRadius: '50%',
            transition: 'transform 0.2s ease',
          }}
          aria-label="User menu"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              (e.currentTarget as HTMLElement).click();
            }
          }}
        >
          <Group gap="sm" style={{ padding: '4px 8px' }}>
            <UserAvatar
              name={user.name}
              role={user.role}
              size="md"
            />
            <IconChevronDown size={14} style={{ color: 'var(--mantine-color-gray-6)' }} />
          </Group>
        </Box>
      </Menu.Target>

      <Menu.Dropdown>
        {/* User Info Section */}
        <Box px="md" py="sm">
          <Group gap="sm">
            <UserAvatar
              name={user.name}
              role={user.role}
              size="lg"
            />
            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={600} lineClamp={1}>
                {user.name}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {user.email}
              </Text>
              <Text
                size="xs"
                c={user.role === 'ADMIN' ? 'red' : user.role === 'COORDINATOR' ? 'blue' : 'green'}
                fw={500}
                mt={2}
              >
                {getRoleLabel(user.role)}
              </Text>
            </Box>
          </Group>
        </Box>

        <Divider />

        {/* Menu Items */}
        <Menu.Item
          leftSection={<IconUser size={16} />}
          onClick={handleProfileClick}
          aria-label="Go to profile page"
        >
          Profile
        </Menu.Item>

        <Menu.Item
          leftSection={<IconLogout size={16} />}
          onClick={onLogout}
          color="red"
          aria-label="Logout from system"
        >
          Logout
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default UserAvatarDropdown;

